import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Toast } from '@zibanu/ui';

interface Notice {
  message: string;
  action?: { label: string; run: () => void } | undefined;
}

const NoticesContext = createContext<{ show(message: string, action?: Notice['action']): void }>({ show: () => undefined });

/** One short message at a time, 32 px clear of Capture; it goes after a few seconds unless it carries an action. */
export function NoticesProvider({ children }: { children: ReactNode }) {
  const [notice, setNotice] = useState<Notice | null>(null);
  const show = useCallback((message: string, action?: Notice['action']) => setNotice({ message, action }), []);
  useEffect(() => {
    if (!notice || notice.action) return;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);
  const value = useMemo(() => ({ show }), [show]);
  return (
    <NoticesContext.Provider value={value}>
      {children}
      {notice && (
        <Toast
          message={notice.message}
          {...(notice.action ? { action: notice.action.label, onAction: notice.action.run } : {})}
          onDismiss={() => setNotice(null)}
        />
      )}
    </NoticesContext.Provider>
  );
}

export function useNotices() {
  return useContext(NoticesContext);
}
